import { PathName } from 'router/paths';

import {
  JWT_SHAPED_VALUE,
  ZOOM_CHAT_IDENTITY_OWNER_KEY,
  ZOOM_CHAT_IDENTITY_OWNER_VERSION,
  ZOOM_CHAT_SCRIPT_SRC,
  ZOOM_CHAT_STORAGE_KEY_PREFIXES,
  ZOOM_CHAT_STORAGE_KEYS,
  ZOOM_CHAT_WEBSITE_DATA_KEYS
} from './zoomChat.constants';
import { cancelPendingZoomChatEscalations } from './zoomChatEscalation';

/**
 * Drops the Zoom chat SDK's cached consumer identity.
 *
 * Zoom keeps its state in localStorage, which `session.clear()` on logout does not touch,
 * so without this a patient's chat identity survives logout — on a shared or family device
 * the next person's chat can resolve to the previous patient. Dispatched from
 * clearZoomChatIdentityMiddleware on `user/clearUser`.
 *
 * Two things worth knowing before editing:
 *
 * 1. Zoom's session token is stored under an unpredictably-named key, so it is found by value
 *    (a JWT-shaped string) rather than by name. redux-persist's own key is excluded, since it
 *    can hold a token too and dropping it would take unrelated persisted slices with it.
 * 2. Do NOT widen this to every Zoom-looking key. Removing keys the SDK relies on for
 *    initialisation (`is_websdk_cache_cleared` and the `OP/*` entries) leaves the widget
 *    half-started: it renders a composer but never presents the welcome form or opens an
 *    engagement. `ZoomWebsdkInstanceId` was checked against that specific risk and is safe:
 *    the widget re-initialises and presents the welcome form as it does for a first-time
 *    visitor. It is also the key that makes this function effective at all — see
 *    ZOOM_CHAT_STORAGE_KEYS for what happens without it.
 */
const REDUX_PERSIST_KEY = 'persist:root';

/**
 * Records that identity was cleared in a document where Zoom's client is still executing.
 *
 * Module scope is the point, not a shortcut. The thing needing eviction is an executed ES module,
 * whose lifetime is exactly this document's — so a signal in module scope survives precisely as long
 * as the problem does, and dies with it. Storage cannot carry it: the central `clearUser` teardown
 * deletes the Zoom keys AND the owner marker, so by the time the replacement patient mounts there is
 * nothing left in storage to notice, and the reload never fires. That is the sequence the deep-link,
 * cross-tab logout and refresh-failure paths all produce.
 */
let evictionOwed = false;

export const isZoomChatSdkLoaded = () =>
  Boolean(document.querySelector(`script[src="${ZOOM_CHAT_SCRIPT_SRC}"]`));

/**
 * The campaign runtime's persisted-session families — the store that carries
 * `state.activeSessionId`, which the runtime restores with no freshness check. Matched by prefix
 * because the middle segments (persist key, apikey, instance) are composed at runtime; see
 * ZOOM_CHAT_STORAGE_KEY_PREFIXES for the derivation.
 */
const isCampaignSessionKey = (key: string) =>
  ZOOM_CHAT_STORAGE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));

/**
 * The unlisted half of the removal set, shared by detection and cleanup as ONE function because
 * the boundary leaks the moment they diverge: a probe narrower than the sweep stamps a new owner
 * over surviving session state (exactly what happened while the campaign prefixes were missing
 * from both — unattributed `zcx-*` state was invisible to the probe, so the claim adopted it),
 * and a sweep narrower than the probe clears less than it detected. The fixed key list is checked
 * by name separately; this covers the names that cannot be listed — campaign-session families by
 * prefix, credential values by JWT shape, with redux-persist's own key excluded because it can
 * hold a token too and dropping it would take unrelated persisted slices with it.
 */
const isRemovableUnlistedKey = (key: string) =>
  isCampaignSessionKey(key) ||
  (key !== REDUX_PERSIST_KEY && JWT_SHAPED_VALUE.test(window.localStorage.getItem(key) ?? ''));

// Same predicate as the cleanup by construction — both build on `isRemovableUnlistedKey`, which
// carries the invariant's rationale.
const hasZoomChatState = () => {
  if (ZOOM_CHAT_STORAGE_KEYS.some((key) => window.localStorage.getItem(key) !== null)) {
    return true;
  }

  return Object.keys(window.localStorage).some(isRemovableUnlistedKey);
};

export const clearZoomChatIdentity = (): boolean => {
  // First, so a Connect tap still waiting for the SDK can never write this patient's context or
  // open a chat after the sweep (weekend review, Sept 28).
  cancelPendingZoomChatEscalations();

  try {
    // Recorded before the keys go, because afterwards there is no way to tell this happened.
    if (isZoomChatSdkLoaded()) {
      evictionOwed = true;
    }

    ZOOM_CHAT_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
    window.localStorage.removeItem(ZOOM_CHAT_IDENTITY_OWNER_KEY);

    // The escalation routing hints carry patient context (the handoff summary and greeting), so
    // they are part of the identity sweep: nothing agent-facing may survive the session.
    Object.values(ZOOM_CHAT_WEBSITE_DATA_KEYS).forEach((key) =>
      window.localStorage.removeItem(key)
    );

    Object.keys(window.localStorage)
      .filter(isRemovableUnlistedKey)
      .forEach((key) => window.localStorage.removeItem(key));

    return true;
  } catch {
    // Storage unavailable (private browsing, quota) or a removal failed part-way. Logout must
    // still complete, so this reports instead of throwing — and the report is load-bearing:
    // claimZoomChatIdentity refuses to stamp an owner over a sweep it cannot prove finished.
    return false;
  }
};

/**
 * The identity-boundary verdict `claimZoomChatIdentity` hands the widget. Only 'ready' may load
 * the SDK; every other value leaves the document without a chat client on purpose:
 * 'eviction-pending' means a reload is in flight and this document must stay clean,
 * 'cleanup-failed' means the previous patient's state could not be fully removed and the widget
 * fails closed rather than chat over it, and 'skipped' means there was no patient to claim for.
 */
export type ZoomChatIdentityClaim = 'ready' | 'eviction-pending' | 'cleanup-failed' | 'skipped';

/**
 * Clears the Zoom identity when it belongs to a different patient, then records the current one —
 * unless a reload is owed or the clear could not finish, in which cases the stamp is withheld and
 * the returned verdict tells the widget not to load the SDK.
 *
 * Called at widget mount rather than at teardown, because teardown cannot cover the most ordinary
 * way a session ends. Portal tokens live in sessionStorage, so closing the tab ends the session
 * without dispatching `user/clearUser` — the middleware never runs, and the Zoom keys in
 * localStorage outlive the session. Checking ownership on the way IN catches that, and every other
 * path that replaces one patient with another, without depending on a teardown having happened.
 *
 * Unattributed state counts as a mismatch, NOT as a first visit. Every browser that used the chat
 * before the owner marker existed holds Zoom keys with no owner, and on a shared device those belong
 * to whoever was last signed in — adopting them would reproduce the exact disclosure the marker is
 * here to prevent, for the whole rollout window. A genuine first visit has no state at all, so it
 * clears nothing; the cost is that the first mount after deploy starts a fresh engagement.
 *
 * A marker naming a DIFFERENT patient clears unconditionally, without consulting
 * `hasZoomChatState()`. The marker is our own writing and is proof on its own that this browser
 * served someone else, so the DECISION no longer depends on recognising Zoom's storage key names.
 * The probe is kept only for the unattributed case, where there is no marker to reason from.
 *
 * Be precise about what that buys. `hasZoomChatState` is deliberately the same predicate as the
 * cleanup (see its comment), so `probe === false` means the clear removes nothing but the marker,
 * and the delta of that case is the eviction — `evictionOwed`, a fresh document for a client still
 * executing with the previous patient's engagement in memory. The removal set now reaches the
 * fixed keys, the campaign-session prefix families (`zcx-customer-chat/`,
 * `zcx-persisted-session/` — read out of the campaign runtime itself) and JWT-bearing values, so
 * the known session-restore routes are cleared. What remains outside all three — a name the
 * cleanup cannot recognise — still survives, and a reload does not touch localStorage. On the
 * tab-close path there is no eviction at all: this runs before `useScript` injects the tag, so
 * nothing is loaded to evict.
 *
 * Provenance differs by half, and the difference decides what re-derivation means: the prefixes
 * are read out of the runtime Zoom serves today and record its version (see
 * ZOOM_CHAT_STORAGE_KEY_PREFIXES); the fixed key list is empirical, gathered under the previous
 * per-entry-point binding. The signed-in preview A/B under the campaign tag is what verifies the
 * combined set — first run 2026-08-07 against runtime v=9773, findings recorded on
 * ZOOM_CHAT_STORAGE_KEYS — and it repeats when Zoom's unversioned loader changes what it serves.
 */
export const claimZoomChatIdentity = (patientId: string): ZoomChatIdentityClaim => {
  if (!patientId) {
    return 'skipped';
  }

  try {
    const rawOwner = window.localStorage.getItem(ZOOM_CHAT_IDENTITY_OWNER_KEY);
    // Only a marker in the current format is trusted; see ZOOM_CHAT_IDENTITY_OWNER_VERSION for
    // why a legacy one attests a narrower sweep than today's. Absent, blank and legacy all read
    // as unattributed, not as a mismatch — each must take the probe path rather than
    // short-circuit past it.
    const owner = rawOwner?.startsWith(ZOOM_CHAT_IDENTITY_OWNER_VERSION)
      ? rawOwner.slice(ZOOM_CHAT_IDENTITY_OWNER_VERSION.length)
      : '';
    const isUnattributed = !owner;
    const ownedByAnotherPatient = !isUnattributed && owner !== patientId;

    const needsClear = ownedByAnotherPatient || (isUnattributed && hasZoomChatState());
    const cleared = needsClear ? clearZoomChatIdentity() : true;

    // Checked BEFORE the stamp, and the stamp is skipped when a reload is coming. The old order —
    // stamp, then reload — left a window the write-back race walks through: the still-executing
    // client can re-persist its keys between the clear and the navigation commit (observed live on
    // the deployed preview, 2026-08-07: `livesdk_persisted_data` and a credential key re-appeared
    // after the logout clear), and a document already stamped for the new patient adopts them
    // silently. Unstamped, those write-backs stay unattributed, so the fresh document's probe
    // sweeps them before `useScript` injects the SDK — and stamps there, where `evictionOwed` is
    // false in the new module, so no second reload can form.
    if (evictionOwed) {
      reloadToEvictZoomChatSdk({ inPlace: true });
      return 'eviction-pending';
    }

    // A failed sweep must not be stamped over. The marker is the one thing that stops the next
    // mount from probing again, so writing it here would attribute the previous patient's
    // surviving state to this one — permanently, because a matching owner never re-probes.
    // Leaving the state unattributed keeps the retry open, and the caller fails the widget
    // closed rather than chat over it.
    if (!cleared) {
      return 'cleanup-failed';
    }

    window.localStorage.setItem(
      ZOOM_CHAT_IDENTITY_OWNER_KEY,
      ZOOM_CHAT_IDENTITY_OWNER_VERSION + patientId
    );

    return 'ready';
  } catch {
    // Storage unavailable (private browsing). Nothing can persist across patients in this state —
    // Zoom cannot write its keys any more than we can read them — so a clean document is safe to
    // host the widget. A document with a client already executing is not: ownership is
    // unverifiable, so evict once. The reloaded document re-enters this function before any
    // injection, finds no client, and mounts clean — bounded at one reload by construction.
    return isZoomChatSdkLoaded() && reloadToEvictZoomChatSdk({ inPlace: true })
      ? 'eviction-pending'
      : 'ready';
  }
};

/**
 * Forces a document load when Zoom's chat client is present, and reports whether it did.
 *
 * Zoom's client is an ES module: removing its script tag does not unload it. After a session ends
 * the module keeps running in this document with the previous patient's engagement, consumer id
 * and transcript in memory, and because it owns the localStorage keys `clearZoomChatIdentity`
 * removes, it can write them back. So the storage clear alone does not hold on a document that
 * survives sign-out, and a fresh document is the only eviction available until Zoom exposes a
 * destroy or logout lifecycle.
 *
 * Only acts when the script is actually in the document, so an ordinary sign-out with no chat
 * loaded keeps its SPA transition. The login page it loads has no script, and the widget cannot
 * mount there without a signed-in patient (the `userId` gate in `useZoomChatAllowed`), so this
 * cannot loop.
 *
 * `inPlace` reloads the current URL instead, for a patient REPLACEMENT rather than a sign-out:
 * there is a live session to keep, so sending it to the login page would be wrong.
 *
 * Callers: sign-out (to the login page), patient replacement, and turning the `zoom-chat-widget`
 * flag off. All three need the same thing for the same reason — an ES module cannot be unloaded, so
 * a document load is the only way to stop a client that is already running.
 */
export const reloadToEvictZoomChatSdk = ({ inPlace = false } = {}): boolean => {
  try {
    if (!document.querySelector(`script[src="${ZOOM_CHAT_SCRIPT_SRC}"]`)) {
      return false;
    }

    if (inPlace) {
      window.location.reload();
    } else {
      window.location.assign(PathName.Login);
    }

    return true;
  } catch {
    // Never block a sign-out on this. The caller falls back to its own navigation.
    return false;
  }
};
